import { useEffect, useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowRight, Building2, ShieldCheck, Users } from "lucide-react"
import { useEvents } from "@/core/active-event"
import { useUserOrgs } from "@/core/orgs"
import { isControlPlaneUser } from "@/core/auth"
import {
  ConsoleShell,
  RailIcon,
  RailLabel,
  railItemClass,
} from "@/components/org/ConsoleShell"
import { SectionPage } from "@/components/settings/primitives"
import { EventCard, ScopeChips } from "@/components/org/EventCard"
import { portalSections, scopeCounts, type Scope } from "@/core/event-scope"
import { requireSession } from "@/lib/auth-guard"

export const Route = createFileRoute("/")({
  beforeLoad: async ({ location, context }) => {
    const user = await requireSession(location.href)
    context.queryClient.setQueryData(["session", "me"], user)
    return { user }
  },
  component: Landing,
})

// Org-less root portal. One section per org the user administers, each with its
// events; pick an event to enter the workspace. Membership-driven (admin-events).
function Landing() {
  const events = useEvents((s) => s.events)
  const loading = useEvents((s) => s.loading)
  const loaded = useEvents((s) => s.loaded)
  const error = useEvents((s) => s.error)
  const loadEvents = useEvents((s) => s.load)
  const clearOrg = useEvents((s) => s.clearOrg)
  const orgs = useUserOrgs((s) => s.orgs)
  const orgsLoaded = useUserOrgs((s) => s.loaded)
  const orgsLoading = useUserOrgs((s) => s.loading)
  const loadOrgs = useUserOrgs((s) => s.load)
  // Same buckets as the tenant page and the iOS hub: default to what's still
  // happening, with Past a chip away rather than padding the list.
  const [scope, setScope] = useState<Scope>("active")

  useEffect(() => {
    if (!loaded && !loading) void loadEvents()
  }, [loaded, loading, loadEvents])
  useEffect(() => {
    if (!orgsLoaded && !orgsLoading) void loadOrgs()
  }, [orgsLoaded, orgsLoading, loadOrgs])
  // Root must be org-less: drop any active org from a prior visit.
  useEffect(() => {
    void clearOrg()
  }, [clearOrg])

  const counts = useMemo(() => scopeCounts(events), [events])

  const sections = useMemo(() => portalSections(events, orgs, scope), [events, orgs, scope])

  // Anything before BOTH lists resolve counts as loading: `loading` alone is
  // false on the very first render (the loads start in effects below), which
  // otherwise painted the empty "no events" portal for a beat — the same defect
  // as the event screens. The server renders the skeleton too (the stores stay
  // empty there by design), so the client's first render matches the SSR HTML.
  const stillLoading = import.meta.env.SSR || !loaded || !orgsLoaded

  return (
    <ConsoleShell
      identity={
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[14px] font-semibold text-plum-900 dark:text-plum-50">
            Organizations
          </span>
        </span>
      }
      actions={<PlatformLink />}
      nav={
        <>
          {/* The rail IS the org list — the same jump the tenant page's
              "All organizations" back-link returns you to. */}
          {orgs.map((o) => (
            <Link
              key={o.id}
              to="/$orgSlug"
              params={{ orgSlug: o.slug }}
              title={o.name}
              className={railItemClass}
            >
              <RailIcon>
                {o.logo ? (
                  <img src={o.logo} alt="" className="h-[18px] w-[18px] rounded object-cover" />
                ) : (
                  <Building2 />
                )}
              </RailIcon>
              <RailLabel>{o.name}</RailLabel>
            </Link>
          ))}
          {orgs.length === 0 && !stillLoading && (
            <p className="hidden px-3 py-2 text-[12px] text-plum-700/45 @[560px]:block dark:text-plum-200/35">
              No organizations
            </p>
          )}
        </>
      }
    >
      <SectionPage title="Your events" description="Pick an event to open the console.">
        {/* Always rendered so the row is part of the page's shape — counts read
            0 until the data lands. */}
        <ScopeChips scope={scope} onScope={setScope} counts={counts} />
        {stillLoading ? (
          <div className="space-y-8">
            {[0, 1].map((s) => (
              <section key={s}>
                <div className="mb-2 flex items-center gap-2 px-1">
                  <Building2 className="h-3.5 w-3.5 text-plum-700/45 dark:text-plum-200/35" />
                  <div className="h-3 w-28 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                </div>
                <ul className="grid gap-2.5 sm:grid-cols-2">
                  {[0, 1].map((i) => (
                    <li key={i}>
                      <div className="flex items-center gap-3 rounded-2xl border border-plum-200/60 bg-white p-3.5 shadow-fluent-sm ring-1 ring-plum-200/20 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
                        <span className="h-11 w-11 shrink-0 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
                        <span className="min-w-0 flex-1 space-y-1.5">
                          <span className="block h-3.5 w-32 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                          <span className="block h-3 w-20 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-[13px] text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
            Couldn't load events: {error}
          </div>
        ) : sections.length === 0 ? (
          <div className="rounded-2xl border border-plum-200/60 bg-white p-8 text-center shadow-fluent ring-1 ring-plum-200/30 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
              <Building2 className="h-5 w-5" />
            </div>
            <p className="text-[14px] font-semibold text-plum-900 dark:text-plum-50">
              Nothing to manage yet
            </p>
            <p className="mt-1 text-[13px] text-plum-700/55 dark:text-plum-200/45">
              This account isn't a member of any organizations.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {sections.map(({ org, events: evs }) => (
              <section key={org?.id ?? "__shared"}>
                {org ? (
                  /* The heading is the way into the tenant page — every event in
                     this org, with its own filters and the create actions. */
                  <Link
                    to="/$orgSlug"
                    params={{ orgSlug: org.slug }}
                    className="group mb-2 inline-flex items-center gap-2 rounded-lg px-1 py-0.5 transition hover:bg-plum-100/60 dark:hover:bg-white/5"
                  >
                    <Building2 className="h-3.5 w-3.5 text-plum-700/45 dark:text-plum-200/35" />
                    <h2 className="text-[11px] font-bold tracking-[0.07em] text-plum-700/55 uppercase transition group-hover:text-plum-900 dark:text-plum-200/40 dark:group-hover:text-plum-50">
                      {org.name}
                    </h2>
                    <ArrowRight className="h-3 w-3 text-plum-700/25 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-plum-200/25" />
                  </Link>
                ) : (
                  /* Crew bucket — static, because there is no tenant page these
                     events' org would let this user open. */
                  <div className="mb-2 inline-flex items-center gap-2 px-1 py-0.5">
                    <Users className="h-3.5 w-3.5 text-plum-700/45 dark:text-plum-200/35" />
                    <h2 className="text-[11px] font-bold tracking-[0.07em] text-plum-700/55 uppercase dark:text-plum-200/40">
                      Shared with you
                    </h2>
                  </div>
                )}
                {evs.length === 0 ? (
                  <p className="px-1 text-[12.5px] text-plum-700/45 dark:text-plum-200/35">
                    No events yet.
                  </p>
                ) : (
                  <ul className="grid gap-2.5 sm:grid-cols-2">
                    {evs.map((e) => (
                      <EventCard
                        key={e.eventId}
                        event={e}
                        orgSlug={e.organizationSlug ?? org?.slug ?? ""}
                      />
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        )}
      </SectionPage>
    </ConsoleShell>
  )
}

// Control-plane shortcut into the platform console (appmanager + sysadmin).
// Gated off the JWT `userRole` claim; renders nothing for everyone else.
function PlatformLink() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    let alive = true
    void isControlPlaneUser().then((ok) => {
      if (alive && ok) setShow(true)
    })
    return () => {
      alive = false
    }
  }, [])
  if (!show) return null
  return (
    <Link
      to="/platform"
      className="inline-flex items-center gap-1.5 rounded-lg border border-plum-200/60 bg-white px-2.5 py-1 text-[12.5px] font-semibold text-plum-700 shadow-fluent-sm transition hover:text-plum-900 dark:border-white/10 dark:bg-surface-raised dark:text-plum-200/70 dark:hover:text-plum-50"
    >
      <ShieldCheck className="h-3.5 w-3.5" /> Platform Admin
    </Link>
  )
}
